import {ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, Output, ViewChild} from '@angular/core';
import {DetectMethodChanges} from '../utils';
import {OpgTradeSuggestion, Run, SideResult, Tab} from './opg-copilot-domain';
import {IOpgCopilotFacade} from './opg-copilot-facade';

// The results area of the OPG CoPilot (CR-20260710-001): renders the active tab's run outcome.
//
// States (from tab.currentRun.outcome):
//   'pending'        -> spinner + "Analyzing…" row (slow must NEVER read as "no trades" — §11);
//   'empty'          -> calm "no trades matched" note — a VALID answer, NOT an error (§6);
//   'terminalFailed' -> failure row with run.failureDetail ("please try again" tone, §11);
//   'succeeded'      -> per-side result columns.
// 'cancelled'/'preSendBlocked' render nothing here (phase-2 parity states; the mock never emits them).
//
// Layout (§6, prohibition §10): call results in a LEFT column, put results in a RIGHT column —
// NEVER mixed in one line; single-side tabs render one column. Each side's result renders
// through ets-opg-trade-stack — a member card stack + a Load-All bar gated to
// single-expiration remaining sets (WI-7). Column headers carry the side name only
// (BF-4/A4: no sample chips anywhere — mock provenance lives in the names).
//
// COURIER (§6): purely presentational — reflects the run's results verbatim; never caps,
// filters, re-ranks, or reshapes them. All child intents bubble up UNCHANGED to the orchestrator.
//
// ISOLATED SIBLING: duplicates the APG results-list / Hedge empty-result idioms WITHOUT
// importing from adjustment-pricing-grid/ or hedging-grid/ (family rule).
// DEFAULT change detection ON PURPOSE (family leaf-owns-CD rule, like the trade card):
// every input here ([tab], [root], [loadedPriceOf]) is a permanently STABLE reference
// while the session mutates the Tab/Run/results IN PLACE — under OnPush, the
// orchestrator's decorated detectChanges() would never reach this subtree (Ivy refreshes
// only CheckAlways/Dirty children) and the run lifecycle would never render.
@Component({
    selector: 'ets-opg-copilot-results-list',
    templateUrl: './opg-copilot-results-list.component.html',
    styleUrls: ['./opg-copilot-results-list.component.scss']
})
export class OpgCopilotResultsListComponent {

    constructor(private readonly _changeDetector: ChangeDetectorRef) {
    }

    // The active tab — this component reads tab.currentRun (state machine) + tab.results (cards).
    @Input() tab: Tab;

    // Host facade, passed through to the cards (leg tickers, loaded-cell price reads).
    @Input() root: IOpgCopilotFacade;

    // Loaded-cell price lookup for a previewed suggestion (grid = tradeable reference, §9);
    // null while the cell is unpriced. Passed through to the cards.
    @Input() loadedPriceOf: (suggestionId: string) => number | null;

    // Whether the per-side stacks render their Load-All / Preview-All bar — a COMPARISON
    // affordance, hidden by 'pick'-model hosts (owner ruling 2026-08-04: the HedgeMatrix
    // Select tab holds one previewed hedge per date/side, so batch-previewing has no meaning).
    @Input() showLoadAll = true;

    // Bubbled child intents — the orchestrator owns preview/load behavior.
    @Output() previewToggled = new EventEmitter<OpgTradeSuggestion>();
    @Output() loadAllRequested = new EventEmitter<OpgTradeSuggestion[]>();
    @Output() unPreviewAllRequested = new EventEmitter<OpgTradeSuggestion[]>();

    // WI-16/A8: on the dual tab each result column collapses to a rail, on its OWN state — the
    // orchestrator's resultsSideCollapsed, INDEPENDENT of the questionnaire's sideCollapsed (owner
    // 2026-07-14; repeals the old §6 lockstep). Collapse = the column header; expand = the rail.
    // Ignored on single-side tabs (nothing to collapse against).
    //
    // Setters, not plain inputs, ONLY to run the height lock below (the mirror of the
    // orchestrator's .duo lock, WI-12): collapsing the SECOND side leaves two bare rails, which
    // would shrink this section to the rail min-height. An @Input setter runs while the DOM
    // still shows the PREVIOUS layout (the child view is refreshed after inputs land), so it is
    // the one place that can still measure the height we are about to lose.
    private _callsCollapsed = false;
    private _putsCollapsed = false;

    @Input()
    set callsCollapsed(value: boolean) {
        this.applyHeightLock(value, this._putsCollapsed);
        this._callsCollapsed = value;
    }

    get callsCollapsed(): boolean {
        return this._callsCollapsed;
    }

    @Input()
    set putsCollapsed(value: boolean) {
        this.applyHeightLock(value, this._callsCollapsed);
        this._putsCollapsed = value;
    }

    get putsCollapsed(): boolean {
        return this._putsCollapsed;
    }

    @Output() callsCollapsedChange = new EventEmitter<boolean>();
    @Output() putsCollapsedChange = new EventEmitter<boolean>();

    // Pinned while BOTH sides are collapsed; null (natural sizing) the moment either expands.
    columnsLockedHeightPx: number | null = null;

    @ViewChild('columnsSection') private _columnsSection: ElementRef<HTMLElement>;

    private applyHeightLock(incomingCollapsed: boolean, otherCollapsed: boolean): void {
        if (incomingCollapsed && otherCollapsed) {
            const measured = this._columnsSection ? this._columnsSection.nativeElement.offsetHeight : 0;
            if (measured > 0) {
                this.columnsLockedHeightPx = measured;
            }
        } else {
            this.columnsLockedHeightPx = null;
        }
    }

    get run(): Run | null {
        return this.tab ? this.tab.currentRun : null;
    }

    // CR-20260830-001 (owner, round 3): when NO state renders (no run yet, or a render-nothing
    // outcome like 'cancelled'), the host element must leave the layout entirely — as a zero-height
    // flex child it still claims a .tab-body gap slot, painting a phantom 12px band under the
    // questionnaire ("looks lifted up a little bit"). display:none via :host(.no-content) removes it.
    @HostBinding('class.no-content')
    get noContent(): boolean {
        return !this.isPending && !this.isEmpty && !this.isFailed && !this.hasResults;
    }

    get isPending(): boolean {
        return !!this.run && this.run.outcome === 'pending';
    }

    get isEmpty(): boolean {
        return !!this.run && this.run.outcome === 'empty';
    }

    get isFailed(): boolean {
        return !!this.run && this.run.outcome === 'terminalFailed';
    }

    get failureText(): string {
        const run = this.run;
        if (run && run.failureDetail) {
            return run.failureDetail;
        }
        return 'The ORION Run Could Not Complete. Please Try Again.';
    }

    // CR-20260902-001: WHY the run is empty, when the skill said so — the sides' notes, one
    // per line (pre-line rendering). Null = keep the generic "Adjust your inputs" sentence.
    get emptyNote(): string | null {
        const run = this.run;
        if (!run) {
            return null;
        }
        const parts: string[] = [];
        if (run.callsNote) {
            parts.push(run.callsNote);
        }
        if (run.putsNote) {
            parts.push(run.putsNote);
        }
        return parts.length ? parts.join('\n') : null;
    }

    // Per-side note for a SUCCEEDED run whose other side came back empty (dual tab).
    get callsNote(): string | null {
        return this.run ? this.run.callsNote : null;
    }

    get putsNote(): string | null {
        return this.run ? this.run.putsNote : null;
    }

    get hasResults(): boolean {
        return !!this.run
            && this.run.outcome === 'succeeded'
            && !!this.tab
            && !!this.tab.results;
    }

    // Calls & Puts tab -> two columns side by side (calls LEFT, puts RIGHT — §6).
    get isDual(): boolean {
        return !!this.tab && this.tab.kind === 'callsAndPuts';
    }

    get showCallsColumn(): boolean {
        if (!this.hasResults) {
            return false;
        }
        return this.isDual || this.tab.kind === 'calls';
    }

    get showPutsColumn(): boolean {
        if (!this.hasResults) {
            return false;
        }
        return this.isDual || this.tab.kind === 'puts';
    }

    // A collapsed side renders as the click-to-expand rail INSTEAD of its column —
    // dual tab only (single-side tabs never collapse).
    get callsRailVisible(): boolean {
        return this.showCallsColumn && this.isDual && this.callsCollapsed;
    }

    get putsRailVisible(): boolean {
        return this.showPutsColumn && this.isDual && this.putsCollapsed;
    }

    // A side ABSENT from a succeeded run is that side's legitimate "no trades" (§6) — the
    // dual-tab column then shows a calm per-side note instead of cards.
    get callsResult(): SideResult | null {
        if (!this.tab || !this.tab.results) {
            return null;
        }
        return this.tab.results.calls || null;
    }

    get putsResult(): SideResult | null {
        if (!this.tab || !this.tab.results) {
            return null;
        }
        return this.tab.results.puts || null;
    }

    // ---- Bubbling handlers. @DetectMethodChanges repaints after the orchestrator's
    // synchronous handling mutated suggestion state in place (family pattern). ----

    @DetectMethodChanges()
    onPreviewToggled(suggestion: OpgTradeSuggestion): void {
        this.previewToggled.emit(suggestion);
    }

    @DetectMethodChanges()
    onLoadAllRequested(members: OpgTradeSuggestion[]): void {
        this.loadAllRequested.emit(members);
    }

    @DetectMethodChanges()
    onUnPreviewAllRequested(members: OpgTradeSuggestion[]): void {
        this.unPreviewAllRequested.emit(members);
    }

    // Header click = COLLAPSE this results side (WI-16/A8). Dual tab only: on a single-side tab the
    // same row is a plain label, so the click is inert (the guard, not the template, decides — one
    // place to reason about, and `isDual` already gates the rail getters).
    @DetectMethodChanges()
    onCollapseClicked(side: 'calls' | 'puts'): void {
        if (!this.isDual) {
            return;
        }
        if (side === 'calls') {
            this.callsCollapsedChange.emit(true);
        } else {
            this.putsCollapsedChange.emit(true);
        }
    }

    // Rail click = EXPAND that side. The other half of the same control pair; the orchestrator
    // routes both into resultsSideCollapsed (the questionnaire's own collapse is untouched — A8).
    @DetectMethodChanges()
    onRailClicked(side: 'calls' | 'puts'): void {
        if (side === 'calls') {
            this.callsCollapsedChange.emit(false);
        } else {
            this.putsCollapsedChange.emit(false);
        }
    }
}
